import {
    FileVM,
    FileStatus,
    FileFragmentVM
} from '../FileAPIClient/Api.types';

export enum FileTrackerStatusEnum {
    None = 0,
    Paused = 1,
    Downloading = 2,
    Verifying = 3,
    Done = 4,
    Error = 5
}

export class DownloadFileTracker {
    constructor(storedFile: FileVM) {
        this.downloadedFilFragmentCount = storedFile.transferedFragments;
        this.storedFile = storedFile;
        this.status = FileTrackerStatusEnum.None;

        if (storedFile.state[storedFile.state.length -1] === FileStatus.DownloadStarted) {
            this.status = FileTrackerStatusEnum.Paused;
        }
    }

    downloadedFilFragmentCount: number = 0;
    queued: boolean = false;
    status: FileTrackerStatusEnum;
    storedFile: FileVM;
    fileFragmentInfos: FileFragmentVM[] | undefined = undefined;
    fileHandle: FileSystemFileHandle | undefined = undefined;
    errorMsg: string = '';
    infoMsg: string = '';
    verifyProgress: number = 0;
    isNewFile: boolean = true;

    async initialFileFragmentNumber(fileFragmentInfos: FileFragmentVM[]): Promise<void> {
        this.fileFragmentInfos = fileFragmentInfos;
        if (this.status === FileTrackerStatusEnum.None || this.status === FileTrackerStatusEnum.Done) {
            this.downloadedFilFragmentCount = 0;
            return;
        }
        const actualSize = await this.actualFileSize()
        const fragmentSize = this.storedFile.fileFragmentSize
        if (this.size > 0 && this.size === actualSize) {
            this.downloadedFilFragmentCount = this.storedFile.fileFragmentCount;
            return;
        }

        if (actualSize % fragmentSize !== 0) {
            throw new Error("Not valid file size, it should be a multiple of the fragment size.");
        }

        this.downloadedFilFragmentCount = actualSize / fragmentSize;
    }

    get hasNext() : boolean {
        if (!this.fileFragmentInfos) {
            throw new Error('Data om filfragment har inte definierats.');
        }
        return this.downloadedFilFragmentCount < this.fileFragmentInfos?.length;
    }

    next() : FileFragmentVM {
        if (!this.hasNext) {
            throw new Error('Finns inget nästa filfragment att hämta.');
        }
        if (!this.fileFragmentInfos) {
            throw new Error('Data om filfragment har inte definierats.');
        }
        const frag = this.fileFragmentInfos.find((f) => f.number === this.nextFragmentNumber);
        this.downloadedFilFragmentCount++;
        return frag;
    }

    abort() {
        this.status = FileTrackerStatusEnum.None
        this.downloadedFilFragmentCount = 0;
    }

    get name(): string {
        if (this.storedFile) {
            return this.storedFile.name;
        } else {
            return '';
        }
    }

    get size(): number {
        if (this.storedFile) {
            return this.storedFile.size;
        } else {
            return 0;
        }
    }

    get nextFragmentNumber(): number {
        return Math.max(0, this.downloadedFilFragmentCount);
    }

    get progress(): number {
        let value = 0;
        if (this.fileFragmentInfos && this.status !== FileTrackerStatusEnum.None && this.status !== FileTrackerStatusEnum.Done) {
            const calculated = (this.downloadedFilFragmentCount / this.fileFragmentInfos.length) * 100;
            const rounded = Math.round(calculated);
            value = Math.max(1, Math.min(rounded, 100)); // info har hämtats. Representerar detta med 1%
        }
        else if (this.storedFile && this.storedFile.transferedFragments > 0) {
            const calculated = (this.downloadedFilFragmentCount / (this.storedFile.fileFragmentCount) - 1) * 100;
            value = Math.round(calculated);
        }
        value = Math.max(0, Math.min(value, 100));
        return value;
    };

    get hasInfo(): boolean {
        if (this.infoMsg && this.infoMsg.length > 0) {
            return true;
        } else {
            return false;
        }
    }

    get hasError(): boolean {
        if (this.errorMsg && this.errorMsg.length > 0) {
            return true;
        } else {
            return false;
        }
    }

    get isPaused(): boolean {
        const pausedStatus = this.status === FileTrackerStatusEnum.Paused || this.status === FileTrackerStatusEnum.Error;
        return pausedStatus && !this.queued;
    }

    get isActive(): boolean {
        return this.status === FileTrackerStatusEnum.Downloading || this.status === FileTrackerStatusEnum.Verifying;
    }

    get isIdle(): boolean {
        return this.status === FileTrackerStatusEnum.None || this.status === FileTrackerStatusEnum.Done;
    }

    get isQueued(): boolean {
        return this.queued;
    }

    clearMessages(): void {
        this.infoMsg = '';
        this.errorMsg = '';
    }

    async actualFileSize() {
        if (!this.fileHandle) throw new Error('The fileHandle does not exist');
        let file = await this.fileHandle.getFile();
        return file.size;
    }
}
